
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactsPageHeader from '@/components/contacts/ContactsPageHeader';
import ContactsContent from '@/components/contacts/ContactsContent';
import ContactsErrorFallback from '@/components/contacts/ContactsErrorFallback';
import ContactsDialogManager from '@/components/contacts/ContactsDialogManager';
import { CallConfirmationDialog } from '@/components/contacts/CallConfirmationDialog';
import { useContactsData } from '@/hooks/contacts/useContactsData';
import { useTwilioCall } from '@/hooks/useTwilioCall';
import { LeadContact } from '@/services/leadContactsService';
import { extractPrimaryPhone } from '@/utils/contactDataHelpers';

const ContactsPage = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'card' | 'list' | 'details'>('list');
  const [selectedContact, setSelectedContact] = useState<LeadContact | null>(null);
  const [contactToCall, setContactToCall] = useState<LeadContact | null>(null);
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  
  // Edit dialog state
  const [contactToEdit, setContactToEdit] = useState<LeadContact | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Add dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  
  // Delete dialog state
  const [contactToDelete, setContactToDelete] = useState<LeadContact | null>(null);

  // Use the contacts data hook to fetch and manage contacts (server-side paginated)
  const {
    filteredContacts,
    searchQuery,
    setSearchQuery,
    departmentFilter,
    setDepartmentFilter,
    departments,
    isLoading,
    error,
    componentError,
    handleDeleteContact,
    handleContactUpdated,
    loadContacts,
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    handlePageChange,
    handlePageSizeChange
  } = useContactsData();

  const paginatedContacts = filteredContacts;


  // Debug logging to identify stuck loading state
  console.log('ContactsPage Debug:', {
    isLoading,
    contactsCount: filteredContacts.length,
    error,
    componentError,
    viewMode
  });

  // Use Twilio calling functionality
  const { makeCall, micPermission } = useTwilioCall();

  const handleRefresh = () => {
    loadContacts();
  };

  const handleReturnToLeads = () => {
    navigate('/app/leads');
  };

  const handleAddContact = () => {
    setIsAddDialogOpen(true);
  };

  const handleEditContact = (contact: LeadContact) => {
    setContactToEdit(contact);
    setIsEditDialogOpen(true);
  };

  const handleViewContact = (contact: LeadContact) => {
    console.log('👁️ ContactsPage: handleViewContact called for contact:', contact.name);
    navigate(`/app/contacts/${contact.id}`);
  };

  const handleDeleteContactWrapper = (contact: LeadContact) => {
    setContactToDelete(contact);
  };

  const handleConfirmDelete = () => {
    if (contactToDelete) {
      handleDeleteContact(contactToDelete.id);
      setContactToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setContactToDelete(null);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setContactToEdit(null);
  };

  const handleContactUpdatedWrapper = async () => {
    try {
      await handleContactUpdated();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error refreshing contact data:', error);
      // Still close the dialog even if refresh fails
      handleCloseEditDialog();
    }
  };

  const handleBack = () => {
    setSelectedContact(null);
    setViewMode('list');
  };

  const handleInitiateCall = (contact: LeadContact) => {
    setContactToCall(contact);
    setIsCallDialogOpen(true);
  };

  const handleConfirmCall = async () => {
    if (!contactToCall) return;

    const phoneNumber = contactToCall.phone_number || extractPrimaryPhone(contactToCall.phone);
    if (!phoneNumber) return;

    setIsInitiatingCall(true);
    
    try {
      const success = await makeCall(phoneNumber);
      if (success) {
        // Close dialog and let topbar show call status
        setIsCallDialogOpen(false);
        setContactToCall(null);
      }
    } catch (error) {
      console.error('Failed to initiate call:', error);
    } finally {
      setIsInitiatingCall(false);
    }
  };

  const handleCloseCallDialog = () => {
    if (!isInitiatingCall) {
      setIsCallDialogOpen(false);
      setContactToCall(null);
    }
  };

  // Show error fallback if there's a component error
  if (componentError) {
    return (
      <ContactsErrorFallback 
        onRefresh={handleRefresh}
        onReturnToLeads={handleReturnToLeads}
      />
    );
  }

  try {
    return (
      <>
        <div className="container mx-auto py-6 space-y-6">
          <ContactsPageHeader 
            viewMode={viewMode}
            onViewChange={setViewMode}
            onAddContact={handleAddContact}
            onRefresh={handleRefresh}
          />
          
          <ContactsContent 
            viewMode={viewMode}
            selectedContact={selectedContact}
            filteredContacts={filteredContacts}
            paginatedContacts={paginatedContacts}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            departmentFilter={departmentFilter}
            onDepartmentChange={setDepartmentFilter}
            departments={departments}
            isLoading={isLoading}
            error={error}
            onAddContact={handleAddContact}
            onEditContact={handleEditContact}
            onViewContact={handleViewContact}
            onDeleteContact={handleDeleteContactWrapper}
            onContactUpdated={handleContactUpdated}
            onBack={handleBack}
            onInitiateCall={handleInitiateCall}
            // Pagination props
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>

        <CallConfirmationDialog
          isOpen={isCallDialogOpen}
          onClose={handleCloseCallDialog}
          onConfirmCall={handleConfirmCall}
          contact={contactToCall}
          isInitiating={isInitiatingCall}
          micPermission={micPermission}
        />

        <ContactsDialogManager
          selectedContact={contactToEdit}
          isEditDialogOpen={isEditDialogOpen}
          onCloseEditDialog={handleCloseEditDialog}
          onContactUpdated={handleContactUpdatedWrapper}
          departments={departments}
          contactToDelete={contactToDelete}
          onDeleteContact={handleConfirmDelete}
          onCancelDelete={handleCancelDelete}
          isAddDialogOpen={isAddDialogOpen}
          onCloseAddDialog={() => setIsAddDialogOpen(false)}
        />
      </>
    );
  } catch (error) {
    console.error('Error in ContactsPage:', error);
    return (
      <ContactsErrorFallback 
        onRefresh={handleRefresh}
        onReturnToLeads={handleReturnToLeads}
      />
    );
  }
};

export default ContactsPage;
